import React, { useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  makeStyles,
  TextField
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { DatePicker } from "@bonliva-crm/ui";
import { ConsultantWorkStatusType } from "@bonliva-crm/shared";
import { useLocale, useGlobalStore } from "@hooks";
import { ConsultantWorkStatusFragmentFragment } from "@bonliva-crm/graphql";

export type WorkStatusFormValues = {
  id?: string;
  type: ConsultantWorkStatusType;
  startDate: Date | null;
  competitorName: string | null;
  reminderDate: Date | null;
};

const initialValues: WorkStatusFormValues = {
  type: null,
  startDate: null,
  competitorName: null,
  reminderDate: null
};

const schema = yup.object().shape({
  type: yup.string().required(),
  startDate: yup.date().required(),
  competitorName: yup.string().nullable(true),
  reminderDate: yup.date().nullable(true)
});

type Props = {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: WorkStatusFormValues) => void;
  editItem?: ConsultantWorkStatusFragmentFragment;
};

export const WorkStatusFormModal = ({ open, onClose, onSubmit, editItem }: Props) => {
  const classes = useStyles();
  const { locale } = useLocale();
  const { cache } = useGlobalStore();

  const form = useForm<WorkStatusFormValues>({
    defaultValues: initialValues,
    mode: "onChange",
    resolver: yupResolver(schema)
  });

  const watchedType = form.watch("type");
  const isCompetitor = watchedType === ConsultantWorkStatusType.WorkingForCompetitor;

  useEffect(() => {
    if (open && editItem) {
      form.reset({
        id: editItem._id,
        type: editItem.type as ConsultantWorkStatusType,
        startDate: editItem.startDate ? new Date(editItem.startDate) : null,
        competitorName: editItem.competitorName ?? null,
        reminderDate: editItem.reminderDate ? new Date(editItem.reminderDate) : null
      });
    }

    if (!open) {
      form.reset(initialValues);
    }
  }, [open, editItem]);

  useEffect(() => {
    if (!isCompetitor) {
      form.setValue("competitorName", null);
    }
  }, [isCompetitor]);

  const handleSubmit = () => {
    onSubmit(form.getValues());
  };

  const isDirty = Object.keys(form.formState.dirtyFields).length > 0;
  const canSubmit = form.formState.isValid && isDirty;
  const isEditing = !!editItem;

  return (
    <Dialog open={open} maxWidth="sm" fullWidth onClose={onClose}>
      <DialogTitle>{isEditing ? locale.editForm : locale.newForm}</DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Controller
              name="type"
              control={form.control}
              render={({ field, fieldState }) => (
                <Autocomplete
                  options={Object.values(ConsultantWorkStatusType)}
                  getOptionLabel={(option) => locale.workStatus.types[option]}
                  value={field.value ?? null}
                  onChange={(_, value) => field.onChange(value)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={locale.workStatus.title}
                      error={!!fieldState.error}
                      required
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="startDate"
              control={form.control}
              render={({ field, fieldState }) => (
                <DatePicker
                  value={field.value}
                  onChange={(date) => field.onChange(date)}
                  label={locale.StartDate}
                  error={!!fieldState.error}
                  required
                  DatePickerProps={{ popperProps: { strategy: "fixed" } }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="reminderDate"
              control={form.control}
              render={({ field, fieldState }) => (
                <DatePicker
                  value={field.value}
                  onChange={(date) => field.onChange(date)}
                  label={locale.workStatus.reminderDate}
                  error={!!fieldState.error}
                  DatePickerProps={{
                    popperProps: { strategy: "fixed" },
                    minDate: new Date()
                  }}
                />
              )}
            />
          </Grid>
          {isCompetitor && (
            <Grid item xs={12}>
              <Controller
                name="competitorName"
                control={form.control}
                render={({ field, fieldState }) => (
                  <Autocomplete
                    options={cache.competitors}
                    value={field.value ?? null}
                    onChange={(_, value) => field.onChange(value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={locale["Works for competitors"]}
                        error={!!fieldState.error}
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  />
                )}
              />
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Box className={classes.btnWrap}>
          <Button color="primary" className={classes.cancelBtn} onClick={onClose}>
            {locale.Cancel}
          </Button>
          <Button
            color="primary"
            variant="contained"
            disabled={!canSubmit}
            onClick={() => {
              if (!canSubmit) {
                form.trigger();
              } else {
                handleSubmit();
              }
            }}
          >
            {locale.Save}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles((theme) => ({
  dialogContent: {
    minHeight: "200px"
  },
  btnWrap: {
    display: "flex",
    justifyContent: "flex-end",
    width: "100%",
    padding: theme.spacing(2),
    gap: theme.spacing(2)
  },
  cancelBtn: {
    marginRight: "auto"
  }
}));
